import React, { FC, VFC } from 'react';

import classnames from 'classnames';
import { Icon as MintelIcon, Tooltip, Text } from 'mintel-react-ui';
import { mintelBlue200, mintelWhite } from 'mintel-tokens';

import styles from './CollapsedNav.scss';
import useTranslate from '../../utils/translations';
import useTracking from '../../utils/useTracking';
import Icon from '../Icon';

type TooltipProps = {
  tooltipText: string;
  trigger: JSX.Element;
};

const NavTooltip: FC<TooltipProps> = ({ trigger, tooltipText }) => {
  return (
    <Tooltip.Hover placement={'right'} short trigger={trigger}>
      <div className={styles.tooltip}>
        <Text.Body>{tooltipText}</Text.Body>
      </div>
    </Tooltip.Hover>
  );
};

type CollapsedNavPops = {
  databookUrl?: string;
  expandedIcon: string;
  navTitles: {
    [key: string]: string;
  };
  showSeriesData: boolean;
  setExpandedIcon: (iconTitle: string) => void;
  showTranslations: boolean;
  showToc: boolean;
};

const CollapsedNav: VFC<CollapsedNavPops> = ({
  databookUrl = '',
  expandedIcon,
  navTitles,
  showSeriesData,
  setExpandedIcon,
  showTranslations,
  showToc,
}) => {
  const {
    trackDatabookLink,
    trackOpeningInPageNav,
    trackSwitchingTabsInInPageNav,
    trackClosingInPageNav,
  } = useTracking();

  const t = useTranslate();
  const languageNavTooltipText = t(
    'This report is available in other languages',
  );

  const handleClick = (whichIcon: string): void => {
    if (expandedIcon) {
      expandedIcon !== whichIcon
        ? trackSwitchingTabsInInPageNav()
        : trackClosingInPageNav();
    } else {
      trackOpeningInPageNav();
    }
    whichIcon = expandedIcon === whichIcon ? '' : whichIcon;
    setExpandedIcon(whichIcon);
  };

  return (
    <div className={styles['collapsed-nav']}>
      {showTranslations && (
        <NavTooltip
          tooltipText={languageNavTooltipText}
          trigger={
            <div
              className={styles['outer-item']}
              data-intercom-target="cpa-translations"
            >
              <button
                className={classnames(styles.sausage, styles['nav-button'], {
                  [styles.expanded]: expandedIcon === navTitles.translation,
                })}
                tabIndex={0}
                onClick={(): void => {
                  handleClick(navTitles.translation);
                }}
                data-testid="translations-icon"
                aria-label={languageNavTooltipText}
              >
                <Icon name="Translation" />
              </button>
            </div>
          }
        />
      )}
      {showToc && (
        <NavTooltip
          tooltipText={navTitles.toc}
          trigger={
            <div
              className={styles['outer-item']}
              data-intercom-target="cpa-toc-menu"
            >
              <button
                className={classnames(
                  styles.sausage,
                  styles['nav-button'],
                  styles['mintel-icon'],
                  {
                    [styles.expanded]: expandedIcon === navTitles.toc,
                  },
                )}
                tabIndex={0}
                onClick={(): void => {
                  handleClick(navTitles.toc); // tracking every time InPageNav is used
                }}
                aria-label={navTitles.toc}
              >
                <MintelIcon name="menu" color={mintelWhite} size={22} />
              </button>
            </div>
          }
        />
      )}
      {databookUrl && (
        <NavTooltip
          tooltipText={navTitles.databook}
          trigger={
            <div
              className={styles['outer-item']}
              data-intercom-target="cpa-view-databook"
            >
              <a
                className={classnames(
                  styles.sausage,
                  styles['nav-link'],
                  styles['mintel-icon'],
                )}
                href={databookUrl}
                onClick={(): void => trackDatabookLink(databookUrl)}
                rel="noreferrer"
                target="_blank"
                aria-label={navTitles.databook}
              >
                <MintelIcon name="databook" color={mintelBlue200} size={20} />
              </a>
            </div>
          }
        />
      )}
      {showSeriesData && (
        <NavTooltip
          tooltipText={navTitles.series}
          trigger={
            <div
              className={styles['outer-item']}
              data-intercom-target="cpa-timeline"
            >
              <button
                className={classnames(
                  styles.sausage,
                  styles['nav-button'],
                  styles['mintel-icon'],
                  {
                    [styles.expanded]: expandedIcon === navTitles.series,
                  },
                )}
                tabIndex={0}
                onClick={(): void => {
                  handleClick(navTitles.series);
                }}
                aria-label={navTitles.series}
              >
                <MintelIcon name="calendar" color={mintelWhite} size={22} />
              </button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default CollapsedNav;
